import {
  IsNotEmpty,
  IsDateString,
  Length,
  ValidateIf,
  IsNumber,
  Min,
  Max,
  IsInt,
} from 'class-validator';
import { Gender, Ethnicity, Menopause } from '.';
import { IsSameOrBeforeNow } from './validators/IsSameOrBeforeNow';

const moment = require('moment'); // Normal import of moment won't work in Jest so use this hack

export class Client {
  static ETHNICITIES: Ethnicity[] = [
    Ethnicity.WHITE,
    Ethnicity.AFRICAN_AMERICAN,
    Ethnicity.HISPANIC,
    Ethnicity.ASIAN,
    Ethnicity.OTHER,
  ];

  // Required operational attributes
  id: string; // uuid4 uniquely identifies client
  practitionerId: string; // the logged in user subject (partiton key)
  createDate: string; // ISO 8601 Date (sort key)
  lastModifiedDate: string; // ISO 8601 Date

  overallPractitionerNotes?: string; // practitioner notes that are not tied to a specific bloodwork

  @IsNotEmpty({ message: 'First Name is required.' })
  @Length(1, 50, { message: 'First Name may not exceed 50 characters.' })
  firstName: string;

  @IsNotEmpty({ message: 'Last Name is required.' })
  @Length(1, 50, { message: 'Last Name may not exceed 50 characters.' })
  lastName: string;

  @IsNotEmpty({ message: 'Birth Gender is required.' })
  gender: Gender;

  @IsDateString({
    message: 'Date of Birth is required and must be a valid Date.',
  })
  @IsSameOrBeforeNow({ message: 'Date of Birth must not be in the future.' })
  dateOfBirth: string; // ISO-8601 date

  @IsNotEmpty({ message: 'Ethnicity is required.' })
  ethnicity: Ethnicity;

  @IsNotEmpty({ message: 'Menopause type is required for females.' })
  @ValidateIf((c: Client) => c.gender === Gender.FEMALE)
  menopause: Menopause;

  @IsNotEmpty({ message: 'Trimester is required for pregnant females.' })
  @IsNumber({}, { message: 'Trimester must be selected.' })
  @Min(0, { message: 'Trimester must be selected.' })
  @ValidateIf((c: Client) => c.menopause === Menopause.PREGNANT)
  trimester: number;

  @IsNotEmpty({ message: 'Elevation is required.' })
  @IsNumber({}, { message: 'Elevation must be numeric.' })
  @Min(0, { message: 'Elevation must be a whole number between 0 and 20,000.' })
  @Max(20000, {
    message: 'Elevation must be a whole number between 0 and 20,000.',
  })
  @IsInt({ message: 'Elevation must be a whole number between 0 and 20,000.' })
  elevation: number;

  @IsNotEmpty({ message: 'Cigarette Smoker is required.' })
  cigaretteSmoker: boolean = false;

  @IsNotEmpty({ message: 'Alcohol Drinker is required.' })
  alcoholDrinker: boolean = false;

  @ValidateIf((c: Client) => c.cigarettesSmoked != null)
  @IsNumber({}, { message: 'Cigarettes smoked must be numeric.' })
  cigarettesSmoked: number;

  /* Changing this to remove the requirement for entering cigarettes smoked - 4-28-26
  @IsNotEmpty({ message: 'Cigarettes smoked is required.' })
  @ValidateIf((c: Client) => c.cigaretteSmoker)
  @IsNumber({}, { message: 'Cigarettes smoked must be numeric.' })
  cigarettesSmoked: number;*/

  @ValidateIf((c: Client) => c.standardDrinks != null)
  @IsNumber({}, { message: 'Standard Drinks must be numeric.' })
  standardDrinks: number;

  static findClient(clientId: string, clients: Client[]): Client {
    return clients.find(client => {
      return client.id === clientId;
    });
  }

  constructor(client?: Client | any) {
    if (!client) {
      this.reset();
    } else {
      Object.keys(client).forEach(key => {
        this[key] = client[key];
      });
    }
  }

  get displayGender(): string {
    if (this.gender === Gender.MALE) {
      return 'Male';
    } else if (this.gender === Gender.FEMALE) {
      return 'Female';
    }
    return undefined;
  }

  get displayMenopause(): string {
    if (this.isMale()) {
      return 'N/A';
    }
    if (this.menopause === Menopause.PREMENOPAUSE) {
      return 'Premenopause';
    } else if (this.menopause === Menopause.POSTMENOPAUSE) {
      return 'Postmenopause';
    } else if (this.menopause === Menopause.PREGNANT) {
      return 'Pregnant';
    }
    return 'Unknown';
  }

  get displayTrimester(): string {
    if (this.menopause !== Menopause.PREGNANT) {
      return 'N/A';
    }
    switch (this.trimester) {
      case 0:
        return 'Unknown';
      case 1:
        return 'First';
      case 2:
        return 'Second';
      case 3:
        return 'Third';
      default:
        break;
    }
    return 'Unknown';
  }

  get displayEthnicity(): string {
    if (this.ethnicity) {
      return this.ethnicity;
    } else {
      return 'Unknown';
    }
  }

  get displayCigaretteesSmoked(): string {
    return `${this.cigarettesSmoked ?? 0}/day`;
  }

  get displayInhaledSmoke(): string {
    return this.cigaretteSmoker ? 'Yes' : 'No';
  }

  get displayAlcoholDrinker(): string {
    return this.alcoholDrinker ? 'Yes' : 'No';
  }

  get displayStandardDrinks(): string {
    return `${this.standardDrinks ?? 0}/week`;
  }

  get displayElevation(): string {
    return this.elevation >= 0 ? `${this.elevation} feet` : `Unknown`;
  }

  get properName(): string {
    return this.lastName && this.firstName
      ? `${this.lastName}, ${this.firstName}`
      : '';
  }

  get name(): string {
    return this.firstName && this.lastName
      ? `${this.firstName} ${this.lastName}`
      : '';
  }

  get age(): number {
    return this.ageAsOf(new Date());
  }

  ageAsOf(date: Date | String): number {
    if (!date || !this.dateOfBirth) {
      return undefined;
    }
    const birthday = moment(this.dateOfBirth);
    return moment(date).diff(birthday, 'years');
  }

  isBornOnOrBefore(date: Date | String): boolean {
    if (!date || !this.dateOfBirth) {
      return false;
    }
    return moment(this.dateOfBirth).isSameOrBefore(moment(date), 'day');
  }

  get displayAge(): string {
    const age = this.age;
    return age !== undefined ? age.toString() : 'Unknown';
  }

  get key(): Object {
    return {
      practitionerId: this.practitionerId,
      createDate: this.createDate,
    };
  }

  isNew(): boolean {
    return this.id == null;
  }

  isAfricanAmerican(): boolean {
    return this.ethnicity
      ? this.ethnicity === Ethnicity.AFRICAN_AMERICAN
      : false;
  }

  isFemale(): boolean {
    return this.gender === Gender.FEMALE;
  }

  isPremenopausal(): boolean {
    return this.isFemale() && this.menopause === Menopause.PREMENOPAUSE;
  }

  isMale(): boolean {
    return this.gender === Gender.MALE;
  }

  reset(): void {
    this.practitionerId = undefined;
    this.createDate = undefined;
    this.lastModifiedDate = undefined;
    this.id = undefined;
    this.firstName = '';
    this.lastName = '';
    this.gender = undefined;
    this.dateOfBirth = undefined;
    this.ethnicity = Ethnicity.NONE;
    this.menopause = undefined;
    this.trimester = undefined;
    this.elevation = undefined;
    this.cigaretteSmoker = undefined;
    this.cigarettesSmoked = undefined;
    this.alcoholDrinker = undefined;
    this.standardDrinks = undefined;
    this.overallPractitionerNotes = undefined;
  }

  isPregnant(): boolean {
    return this.menopause === Menopause.PREGNANT;
  }

  isTooYoungToBePregnant(): boolean {
    return this.isPregnant() && this.age < 15;
  }

  isTooOld(): boolean {
    return this.age > 200;
  }
}
